import {
  IInitService,
  IUser,
  IUserRoleService,
  ROLES,
  TIDBConnection,
  TPreferences,
  TRole,
} from '@aksia/infrastructure';
import { User } from '@aksia/models';
import { computed, inject, Injectable, signal } from '@angular/core';
import { IndexedDBService } from '../browser/indexed-db.service';
import { concatMap, from, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserRoleService implements IUserRoleService, IInitService {
  //#region Injections

  private readonly indexedDBSvc = inject(IndexedDBService);

  //#endregion

  //#region ------------ Properties ----------------

  //#region User Properties

  readonly aksiaFrontendDB: TIDBConnection = {
    name: 'AksiaFrontend',
    version: 1,
    tables: [
      { name: 'User', keyPath: 'userName', autoIncrement: false },
      { name: 'Preferences', keyPath: 'userName', autoIncrement: false },
    ],
  };
  readonly activeUser = signal<User | undefined>(undefined);
  readonly isAuthenticated = computed(() => !!this.activeUser());
  readonly redirectAfterLogin = signal<string | undefined>(undefined);

  //#endregion

  //#region Role Properties

  readonly activeRole = computed<TRole | undefined>(
    () =>
      this.impersonates() ??
      this.activeUser()?.roles?.find((role: TRole) =>
        (role as string)?.startsWith('O2'),
      ),
  );
  readonly impersonates = signal<TRole | undefined>(undefined);

  readonly userRolesInfo = computed(() => {
    const roles = this.activeUser()?.roles ?? [];
    let userRolesInfo = roles.map((role) => role)?.join(', ') || 'No Roles';
    if (this.impersonates()) {
      userRolesInfo += ` (\r\n\r\nImpersonating: ${this.impersonates()})`;
    }
    return userRolesInfo;
  });
  //#endregion

  //#region Preferences Properties

  readonly preferences = signal<TPreferences | undefined>(undefined);

  //#endregion

  //#endregion

  //#region ---------------- Methods ----------------

  init(): Observable<any> {
    const getFirstUser = () =>
      from(this.indexedDBSvc.getAll(this.aksiaFrontendDB.name, 'User')).pipe(
        concatMap((users) => {
          if (users && users.length > 0) {
            this.setActiveUser(users.at(0) as IUser);
          }
          return of(null);
        }),
      );
    return from(this.indexedDBSvc.openDatabase(this.aksiaFrontendDB)).pipe(
      concatMap((opened) => {
        if (opened) {
          return getFirstUser();
        } else {
          return from(
            this.indexedDBSvc.createDatabase(this.aksiaFrontendDB),
          ).pipe(
            concatMap((created) => {
              if (!created) {
                return of(null);
              }
              return getFirstUser();
            }),
          );
        }
      }),
    );
  }

  //#region User Methods
  readonly initActiveUser = (user: IUser) => {
    this.setActiveUser(user);
    this.saveActiveUser(user);
  };

  readonly setActiveUser = (user: IUser) => {
    this.activeUser.set(new User(user));
    this.loadUserPreferences(user);
    this.impersonates.set(undefined);
  };

  private saveActiveUser(user: IUser) {
    this.indexedDBSvc.update(this.aksiaFrontendDB.name, 'User', user);
  }

  readonly clearActiveUser = () => {
    this.activeUser.set(undefined);
    this.impersonates.set(undefined);
    this.indexedDBSvc.delete(this.aksiaFrontendDB.name, 'User');
  };
  //#endregion

  //#region Role Methods
  readonly isInRole = (role: TRole): boolean => {
    return (
      [...(this.activeUser()?.roles ?? []), this.impersonates()]?.some(
        (userRole) => userRole === role,
      ) ?? false
    );
  };

  readonly isAdmin = (): boolean => {
    return this.activeUser()?.roles?.includes(ROLES.ADMIN) ?? false;
  };

  readonly impersonate = (role: TRole | unknown) => {
    this.impersonates.set(role as TRole);
    this.saveUserPreferences({ impersonatedRole: this.impersonates() });
  };
  //#endregion

  //#region Preferences Methods
  readonly saveUserPreferences = (preferences: Partial<TPreferences>) => {
    this.preferences.update((currentPreferences) => ({
      ...(currentPreferences ?? {}),
      ...preferences,
    }));
    this.indexedDBSvc.update(this.aksiaFrontendDB.name, 'Preferences', {
      ...this.preferences(),
      userName: this.activeUser()?.userName,
    });
  };

  private loadUserPreferences = (user: IUser) => {
    this.indexedDBSvc
      .get(this.aksiaFrontendDB.name, 'Preferences', user.userName!)
      .then((prefs) => {
        if (prefs) {
          this.preferences.set(prefs as TPreferences);
          this.impersonates.set(this.preferences()?.impersonatedRole);
        } else {
          this.preferences.set(undefined);
        }
      });
  };
  //#endregion

  //#endregion
}
